/* eslint-disable eqeqeq */
import React from 'react';
import { useCurrentCourse } from '@reducers/courses/hooks';
import { useIsAssigned } from '@reducers/lessonAssignedActivities/hooks';
import { useSectionStudent } from '@reducers/sectionStudent/hooks';
import { useExamScore } from '@reducers/sections/hooks';
import { useIsTeacher, useUser, useUserRole } from '@reducers/user/hooks';
import { useIsLoadingCourses } from '@reducers/courses/hooks';
import { useParams, useHistory } from 'react-router-dom';
import useLearnosityQuestionCT from '@utils/hooks/useLearnosityQuestionCT';
import {
  actions as activityActions,
  selectors as activitySelectors,
} from '@reducers/activity';
import { useDispatch, useSelector } from 'react-redux';
import { actions as groupActivityAction } from '@reducers/groupActivity';
import { actions as CurriculumLessonActions } from '@reducers/curriculumLessons';
import { useIsAdaptiveLearnosityPracticeEnabled } from '@reducers/curriculums/hooks';
import { useSearchParams } from '@utils/hooks/useSearchParams';
import {
  LessonPracticeStatus,
  LessonTabs,
  UnitMetaObject,
  UnitObjectType,
  ScoreObjectType,
} from '../Types';
import { useCurriculumDetails, useIsCurriculumLoading } from '@reducers/curriculums/hooks';
import {
  useGetEducationalResources,
  useGetLearnosityAdaptivePracticeQuestions,
  useGetLessonDetail,
  useGetSectionUnitMeta,
  useGetStandards,
} from './query';
import {
  checkHasDifference,
  checkLessonCompletion,
  practiceStudentStatusHelper,
  practiceTeacherStatusHelper,
} from '../utils';

const unitMetaObjectPlaceholder = {
  lesson_practice: false,
  group_activity: false,
  unit_practice: false,
  lesson_slides: false,
} as UnitMetaObject;

const unitDetailsPlaceholder = {
  id: 0,
  name: '',
  meta: unitMetaObjectPlaceholder,
} as UnitObjectType;

export const useGetUnitDetails = () => {
  const { unitId, subjectId } = useParams();
  const { data: unitData } = useGetSectionUnitMeta();

  if (!unitData?.length) {
    return unitDetailsPlaceholder;
  }
  const unitsBySubjectId = unitData.find((el) => el.id == subjectId)?.units;

  const unitDetailsObject = unitsBySubjectId?.find((unit) => unit.id == unitId);

  return unitDetailsObject as UnitObjectType;
};

export const useShouldShowStandardPracticeTab = (): boolean => {
  const { data: lessonDetail } = useGetLessonDetail();
  const { hasStandardsPractice } = useLearnosityQuestionCT({ lessonDetail });

  const courseDetails = useCurrentCourse();
  const hasStandardPracticeEnabled = courseDetails?.standard_practice_enabled;

  return hasStandardsPractice && hasStandardPracticeEnabled;
};

export const useShouldShowAdaptivePracticeTab = (): boolean => {
  const { data: lessonDetails } = useGetLessonDetail();
  const unitDetails = useGetUnitDetails();

  const unitHasLessonPractice = unitDetails?.meta?.lesson_practice;
  const lessonCanPractice = lessonDetails?.can_practice;
  // `can_practice` only counts the lesson's skills, so it can be true with an empty question bank.
  const lessonHasQuestions = lessonDetails?.has_adaptive_practice_questions === true;

  return !!(lessonCanPractice && unitHasLessonPractice && lessonHasQuestions);
};

export const useShouldShowLearnosityAdaptivePracticeTab = (): boolean => {
  const isAdaptiveLearnosityPracticeEnabled = useIsAdaptiveLearnosityPracticeEnabled();
  const { data: adaptiveLearnosityQuestions } = useGetLearnosityAdaptivePracticeQuestions();

  return isAdaptiveLearnosityPracticeEnabled && !!adaptiveLearnosityQuestions?.length;
};

/**
 * Intervention tracks the lesson's in-app activities, so teachers only see it when this
 * lesson has at least one: an online group activity, adaptive practice or standard practice.
 * Worksheets alone do not count, and neither does the unit's group activity setting.
 */
export const useShouldShowInterventionTab = (): boolean => {
  const isTeacher = useIsTeacher();
  const { data: lessonDetails } = useGetLessonDetail();
  const shouldShowAdaptivePracticeTab = useShouldShowAdaptivePracticeTab();
  const shouldShowLearnosityAdaptivePracticeTab = useShouldShowLearnosityAdaptivePracticeTab();
  const shouldShowStandardPracticeTab = useShouldShowStandardPracticeTab();
  const hasOnlineGroupActivity = lessonDetails?.has_online_group_activity === true;

  return (
    isTeacher &&
    !!(hasOnlineGroupActivity || shouldShowAdaptivePracticeTab || shouldShowLearnosityAdaptivePracticeTab || shouldShowStandardPracticeTab)
  );
};

export const useShouldShowStudyHallTab = (): boolean => {
  const { data: oers } = useGetEducationalResources();

  return !!oers?.length;
};

/**
 * A react-query result counts as settled when it is neither loading nor serving
 * `placeholderData`. An idle (disabled) query is settled on purpose: it will never
 * produce data, so whatever it contributes is a definitive answer, not a pending one.
 * An errored query is settled too, otherwise a failed request would wedge the guard
 * forever.
 */
const isQuerySettled = (query: {
  isLoading?: boolean;
  isPlaceholderData?: boolean;
}): boolean => !query?.isLoading && !query?.isPlaceholderData;

/**
 * Per tab, whether every async source that tab's availability condition actually reads has
 * settled.
 *
 * Those conditions coerce "not loaded yet" into `false`, which makes "this tab is
 * unavailable" indistinguishable from "we have not heard back yet". That is what broke the
 * OGA/OLP deep link: the tab guard rewrote `?tab=olp` to `?tab=lesson` before the data
 * landed, and because `lesson` is unconditionally valid nothing ever sent the student back.
 *
 * This is deliberately per tab rather than one global flag. A global flag makes every deep
 * link wait on data it does not read, so a single source that never settles blocks the
 * guard for every tab. `?tab=olp` only needs the lesson detail; it should not wait on the
 * curriculum.
 *
 * The redux slices are asked for their own loading flags rather than having emptiness
 * inferred as pending. `getCurriculumById` returns `{}` both for "absent" and for "not
 * loaded", so treating `{}` as pending would leave the guard permanently blocked for any
 * course whose curriculum genuinely is not in the store.
 */
export const useTabConditionsResolved = (): Record<string, boolean> => {
  const lessonDetailQuery = useGetLessonDetail();
  const sectionUnitMetaQuery = useGetSectionUnitMeta();
  const educationalResourcesQuery = useGetEducationalResources();
  const adaptivePracticeQuestionsQuery = useGetLearnosityAdaptivePracticeQuestions();
  const courseDetails = useCurrentCourse();
  const isLoadingCourses = useIsLoadingCourses();
  const curriculumDetails = useCurriculumDetails();
  const isLoadingCurriculum = useIsCurriculumLoading();
  const userRole = useUserRole();
  // Feeds `useShouldShowStandardPracticeTab`, and is not covered by Suspense.
  const { isResolved: areStandardsPracticeQuestionsResolved } = useLearnosityQuestionCT({
    lessonDetail: lessonDetailQuery?.data,
  });

  const isLessonDetailResolved = isQuerySettled(lessonDetailQuery);
  // The one that actually bites: `placeholderData: []` makes this query resolve instantly
  // without suspending, so the first render sees an empty unit list and every `meta` flag
  // reads false.
  const isUnitMetaResolved = isQuerySettled(sectionUnitMetaQuery);
  const areEducationalResourcesResolved = isQuerySettled(educationalResourcesQuery);
  const areAdaptiveQuestionsResolved = isQuerySettled(adaptivePracticeQuestionsQuery);
  // Having the data is enough on its own; otherwise fall back to the slice's loading flag
  // so a genuinely absent record still counts as a definitive answer. Both selectors
  // default to `{}` when the record is missing (`getCourseDetailById`, `getCurriculumById`),
  // so emptiness has to be measured rather than truthiness: `{}` is truthy and would
  // otherwise report as loaded while a fetch is still in flight.
  const isCourseResolved =
    Object.keys(courseDetails ?? {}).length > 0 || !isLoadingCourses;
  // The curriculum is looked up by `courseDetails.curriculum`, so it cannot be resolved
  // before the course is. While the course is still loading that id is `undefined`, the
  // lookup returns `{}`, and no curriculum fetch can have been dispatched yet, so the
  // loading flag is false too. Without the course gate that combination reads as a
  // definitive "no curriculum" and bounces a deep link to the adaptive practice tab.
  const isCurriculumResolved =
    isCourseResolved &&
    (Object.keys(curriculumDetails ?? {}).length > 0 || !isLoadingCurriculum);
  // `getUserRole` reads through `userData || {}`, so the role is `undefined` until the user
  // slice is populated, and `useIsTeacher()` reports false in the meantime. Tabs whose
  // availability depends on the role must wait, or a teacher deep-linking to the lesson
  // plan is redirected away before their own role is known.
  const isUserRoleResolved = userRole !== undefined && userRole !== null;

  return React.useMemo(
    () => ({
      [LessonTabs.LessonPlan]: isUserRoleResolved,
      [LessonTabs.Lesson]: true,
      [LessonTabs.LessonPractice]: isLessonDetailResolved,
      // Embeds `!shouldShowAdaptiveLearnosityPracticeTab`, so it inherits everything that
      // tab depends on.
      [LessonTabs.Practice]:
        isLessonDetailResolved &&
        isUnitMetaResolved &&
        areStandardsPracticeQuestionsResolved &&
        isCourseResolved &&
        areAdaptiveQuestionsResolved &&
        isCurriculumResolved,
      // Reads `isTeacher` plus every practice condition, so it waits on all of them and the role.
      [LessonTabs.Intervention]:
        isLessonDetailResolved &&
        isUnitMetaResolved &&
        areStandardsPracticeQuestionsResolved &&
        isCourseResolved &&
        areAdaptiveQuestionsResolved &&
        isCurriculumResolved &&
        isUserRoleResolved,
      [LessonTabs.StudyHall]: areEducationalResourcesResolved,
      [LessonTabs.LearnosityAdaptivePractice]: areAdaptiveQuestionsResolved && isCurriculumResolved,
    }),
    [
      isLessonDetailResolved,
      isUnitMetaResolved,
      areEducationalResourcesResolved,
      areAdaptiveQuestionsResolved,
      areStandardsPracticeQuestionsResolved,
      isCourseResolved,
      isCurriculumResolved,
      isUserRoleResolved,
    ],
  );
};

const scorePlaceholderObject = { totalScore: 0, totalMaxScore: 0 } as ScoreObjectType;

export const usePracticeTotalScoreCalculator = (): ScoreObjectType => {
  const examScore = useExamScore();

  if (!examScore || !examScore.length) {
    return scorePlaceholderObject;
  }
  const [totalScore, totalMaxScore] = examScore.reduce(
    (acc, { score, max_score: maxScore }) => {
      acc[0] += score;
      acc[1] += maxScore;
      return acc;
    },
    [0, 0],
  );
  return { totalScore: Math.round(totalScore), totalMaxScore: Math.round(totalMaxScore) };
};

export const usePracticeStatus = (): LessonPracticeStatus | null => {
  const isAssigned = useIsAssigned();
  const examScore = useExamScore();
  const isTeacher = useIsTeacher();
  const studentList = useSectionStudent();

  if (isTeacher) {
    return practiceTeacherStatusHelper(examScore, studentList);
  }

  return practiceStudentStatusHelper(examScore, isAssigned);
};

export const useGetActivities = () => {
  const studentList = useSectionStudent();
  const { sectionId, lessonId: lessonGroupId, unitId, courseId } = useParams();
  const dispatch = useDispatch();
  const courseData = useCurrentCourse();
  const getActivities = groupActivityAction.getGroupActivities;
  const isTeacher: boolean = useIsTeacher();
  const user = useUser();
  const { data } = useGetLessonDetail();
  const lessonId = data?.lessons[0]?.id;

  React.useEffect(() => {
    // TODO: will remove studentList condition after changing the way we create OLP activity.
    if (!lessonId || !studentList.length) {
      return;
    }
    dispatch(
      getActivities(
        sectionId,
        lessonGroupId,
        lessonId,
        user?.id,
        unitId,
        courseData?.curriculum,
        isTeacher,
        lessonGroupId,
      ),
    );
  }, [sectionId, lessonId, unitId, courseId, studentList.length, lessonGroupId]);
};

export const useGetUnitMetaObject = () => {
  const { unitId, subjectId } = useParams();
  const { data: unitData } = useGetSectionUnitMeta();
  if (!unitData?.length) {
    return unitMetaObjectPlaceholder;
  }

  const unitsBySubjectId = unitData.find((el) => el.id == subjectId)?.units;

  const unitMetaObject = unitsBySubjectId?.find((unit) => unit.id == unitId)?.meta;

  return unitMetaObject as UnitMetaObject;
};

export const useInsertLessonActivitySummary = () => {
  const { lessonId, unitId, subjectId } = useParams();
  const insertLessonActivitySummary = activityActions?.insertLessonActivitySummary;
  const dispatch = useDispatch();

  return React.useCallback(
    (status) =>
      dispatch(
        insertLessonActivitySummary({
          unit: unitId,
          id: lessonId,
          subject: subjectId,
          completed: status,
        }),
      ),
    [dispatch, insertLessonActivitySummary, lessonId, subjectId, unitId],
  );
};

export const useLessonActivitySummaryList = () => {
  const summaryData = useSelector((state) =>
    activitySelectors.getLessonAcitivitySummaryList(state),
  );
  return summaryData;
};

export const useLessonActivityById = () => {
  const lessonActivityById = useSelector((state: any) =>
    activitySelectors.lessonActivityById(state),
  );
  return lessonActivityById;
};

export const useHandleFinishStepOne = () => {
  const isTeacher = useIsTeacher();
  const { lessonId, subjectId, unitId } = useParams();
  const lessonActivitySummaryList = useLessonActivitySummaryList();
  // TODO: check if we can replace this to get query
  const lessonActivityById = useLessonActivityById();
  const { data: lessonDetail } = useGetLessonDetail();
  const hasLessonCompleted = React.useMemo(
    () => checkLessonCompletion(lessonActivitySummaryList, lessonId, subjectId, unitId),
    [lessonId, subjectId, unitId, lessonActivitySummaryList],
  );
  const insertLessonActivitySummary = useInsertLessonActivitySummary();
  return React.useCallback(
    (subLessonId: number | string, status: boolean) => {
      const hasDifference = checkHasDifference(
        lessonActivityById,
        lessonDetail,
        subLessonId,
      );
      if (hasLessonCompleted || isTeacher || hasDifference) {
        insertLessonActivitySummary(status);
      }
    },
    [isTeacher, lessonActivityById, lessonDetail, hasLessonCompleted],
  );
};

export const useReduxAttendanceDataUpdater = () => {
  const { sectionId, lessonId: lessonGroupId } = useParams();
  const dispatch = useDispatch();
  const getAttendance = CurriculumLessonActions.curriculumLessonGetAttendance;

  React.useEffect(() => {
    dispatch(getAttendance(lessonGroupId, sectionId));
  }, [sectionId, lessonGroupId]);
};

export const useLessonStandards = () => {
  const { data: lessonDetail } = useGetLessonDetail();
  return React.useMemo(
    () => lessonDetail?.standards?.map((standard) => standard.code) || [],
    [lessonDetail?.standards],
  );
};

export const useStandardName = () => {
  const courseData = useCurrentCourse();
  const standardId = courseData?.syllabus ?? 0;
  const { data: standards } = useGetStandards();
  return React.useMemo(
    () => standards?.find((el) => el.id == standardId)?.name,
    [standardId, standards],
  );
};

export const useLessonSkills = () => {
  const { data: lessonDetail } = useGetLessonDetail();
  return React.useMemo(() => lessonDetail?.skills || [], [lessonDetail?.skills]);
};

type NavigateToLessonProps = {
  tabKey?: string;
  pacingGuideId?: string;
};

export const useNavigateToLessonTab = () => {
  const searchParams = useSearchParams();
  const history = useHistory();

  return ({ tabKey, pacingGuideId }: NavigateToLessonProps = {}) => {
    const finalTabKey = tabKey ?? searchParams.tab;
    const finalPacingGuideId = pacingGuideId ?? searchParams['pacing-guide-id'];
    history.push({
      pathname: history.location.pathname,
      search: `?tab=${finalTabKey}${
        finalPacingGuideId ? `&pacing-guide-id=${finalPacingGuideId}` : ''
      }`,
    });
  };
};
